import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import NPIButton from 'src/components/Button';
import { DEFAULT_BORDER } from 'src/constants';
import { isIreland } from 'src/lib/countryConfig';
import EditPaymentDetailsModalContent from 'src/pages/ViewPartnerPage/components/PaymentDetails/EditPaymentDetailsModalContent';

import HeaderBackground from '../../assets/images/backgrounds/HeaderBackground.jpg';

import { ReusableGridProps } from './types';

const ReusableGrid = ({
    title,
    children,
    addRecordButtonConfig,
    hasView,
    xs = 6,
    maxWidth,
    hasBackGroundImage,
    handleEdit,
    showEditIcon,
    setViewPaymentDetails,
    paymentDetailsInitialValues,
    handleEditPaymentDetails,
    hasEditPaymentDetails = typeof handleEditPaymentDetails === 'function',
    enableContainer = true,
}: ReusableGridProps) => {
    const [openEditModal, setOpenEditModal] = useState(false);
    const theme = useTheme();

    const handleViewPaymentDetails = () => {
        if (setViewPaymentDetails) {
            setViewPaymentDetails((prevState: boolean) => {
                return !prevState;
            });
        }
    };

    const handleEditModal = () => {
        setOpenEditModal(!openEditModal);
    };

    return (
        <Grid
            item
            xs={xs}
            maxWidth={maxWidth ? '100%' : null}
            sx={{
                ...DEFAULT_BORDER,
                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: hasBackGroundImage ? `url(${HeaderBackground})` : null,
            }}
            {...(enableContainer ? { container: true } : {})} // Conditionally spread `container` prop
        >
            <Grid item container sx={{ padding: '24px 24px' }}>
                {title ? (
                    <Grid
                        item
                        xs={
                            addRecordButtonConfig
                                ? 9
                                : hasEditPaymentDetails || showEditIcon
                                  ? 11
                                  : 12
                        }
                    >
                        <Typography
                            sx={{
                                fontSize: '21px',
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {title}
                        </Typography>
                    </Grid>
                ) : null}
                {showEditIcon && (
                    <Grid item xs={1} display="flex" justifyContent="right" gap={1}>
                        <EditIcon
                            onClick={handleEdit}
                            sx={{
                                fontSize: '24px',
                                fontWeight: 'bold',
                                color: '#4a4a4a',
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: '#2a2a2a',
                                },
                            }}
                        />
                    </Grid>
                )}
                {hasEditPaymentDetails && (
                    <Grid item xs={1} display="flex" justifyContent="right" gap={1}>
                        {hasView && <VisibilityOutlinedIcon onClick={handleViewPaymentDetails} />}
                        {hasEditPaymentDetails && isIreland && (
                            <EditIcon
                                onClick={handleEditModal}
                                sx={{
                                    '&:hover': {
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        )}
                        {hasEditPaymentDetails && (
                            <Dialog
                                open={openEditModal}
                                onClose={handleEditModal}
                                aria-labelledby="edit-payment-details-dialog"
                                sx={{
                                    '& .MuiDialog-paper': {
                                        width: '400px',
                                    },
                                }}
                            >
                                <DialogContent>
                                    <EditPaymentDetailsModalContent
                                        handleClose={handleEditModal}
                                        handleAction={() => {}}
                                        buttonText="Save Changes"
                                        message="Update your payment details"
                                        initialValues={paymentDetailsInitialValues}
                                        handleFormSubmit={handleEditPaymentDetails}
                                    />
                                </DialogContent>
                            </Dialog>
                        )}
                    </Grid>
                )}

                {addRecordButtonConfig && (
                    <Grid item xs={3} display="flex" justifyContent="right">
                        <NPIButton
                            startIcon={<AddIcon />}
                            label={addRecordButtonConfig.addRecordButtonText}
                            onClick={addRecordButtonConfig.addRecordCallBack}
                        />
                    </Grid>
                )}
            </Grid>

            {children}
        </Grid>
    );
};

export default ReusableGrid;
